import { useEffect, useRef } from 'react';

interface FloatingShapesProps {
  isDarkMode: boolean;
}

interface Shape {
  x: number;
  y: number;
  size: number;
  rotation: number;
  rotationSpeed: number;
  velocityX: number;
  velocityY: number;
  type: 'triangle' | 'square' | 'circle';
  color: string;
}

type ShapeDrawer = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number, rotation: number) => void;

const drawTriangle: ShapeDrawer = (ctx, x, y, size, rotation) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.beginPath();
  ctx.moveTo(0, -size / 2);
  ctx.lineTo(-size / 2, size / 2);
  ctx.lineTo(size / 2, size / 2);
  ctx.closePath();
  ctx.restore();
};

const drawSquare: ShapeDrawer = (ctx, x, y, size, rotation) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.beginPath();
  ctx.rect(-size / 2, -size / 2, size, size);
  ctx.closePath();
  ctx.restore();
};

const drawCircle: ShapeDrawer = (ctx, x, y, size) => {
  ctx.beginPath();
  ctx.arc(x, y, size / 2, 0, Math.PI * 2);
  ctx.closePath();
};

const SHAPE_DRAWERS: Record<Shape['type'], ShapeDrawer> = {
  triangle: drawTriangle,
  square: drawSquare,
  circle: drawCircle,
};

function wrapEdges(shape: Shape, width: number, height: number) {
  if (shape.x < -shape.size) shape.x = width + shape.size;
  if (shape.x > width + shape.size) shape.x = -shape.size;
  if (shape.y < -shape.size) shape.y = height + shape.size;
  if (shape.y > height + shape.size) shape.y = -shape.size;
}

/**
 * Floating geometric shapes - retro 80s aesthetic
 */
const FloatingShapes = ({ isDarkMode }: FloatingShapesProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Create shapes
    const shapes: Shape[] = [];
    const numShapes = 12;

    const colors = [
      'rgba(255, 40, 145, 0.4)',  // Hot pink
      'rgba(0, 242, 254, 0.4)',    // Cyan
      'rgba(139, 0, 139, 0.4)',    // Purple
    ];

    for (let i = 0; i < numShapes; i++) {
      shapes.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: 20 + Math.random() * 60,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.02,
        velocityX: (Math.random() - 0.5) * 0.5,
        velocityY: (Math.random() - 0.5) * 0.5,
        type: ['triangle', 'square', 'circle'][Math.floor(Math.random() * 3)] as Shape['type'],
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }

    let animationId: number;

    const draw = () => {
      const { width, height } = canvas;
      ctx.clearRect(0, 0, width, height);

      shapes.forEach((shape) => {
        // Update position
        shape.x += shape.velocityX;
        shape.y += shape.velocityY;
        shape.rotation += shape.rotationSpeed;

        // Wrap around edges
        wrapEdges(shape, width, height);

        // Draw shape
        ctx.fillStyle = shape.color;
        ctx.strokeStyle = shape.color.replace('0.4', '0.6');
        ctx.lineWidth = 2;

        SHAPE_DRAWERS[shape.type](ctx, shape.x, shape.y, shape.size, shape.rotation);

        ctx.fill();
        ctx.stroke();
      });

      animationId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, [isDarkMode]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
      }}
    />
  );
};

export default FloatingShapes;
